import { healthBehaviourSchema, LIBRARY_KINDS } from "@healthos/shared";
import { z } from "zod";
export const LibraryKind = z.enum(LIBRARY_KINDS);
export const LibraryMetadata = z.record(z.string(), z.unknown());
export const ContentRatingVote = z.enum(["up", "down"]);
export const LibraryRatingSummary = z.object({
    upCount: z.number().int().nonnegative(),
    downCount: z.number().int().nonnegative(),
    totalCount: z.number().int().nonnegative(),
    approvalPercent: z.number().int().min(0).max(100).nullable(),
    viewerVote: ContentRatingVote.nullable(),
});
export const LibraryItem = z.object({
    id: z.string().uuid(),
    contentId: z.string().min(1),
    kind: LibraryKind,
    title: z.string(),
    summary: z.string().nullable(),
    imageUrl: z.string().nullable(),
    metadata: LibraryMetadata,
    dietaryLabels: z.array(z.string()).default([]),
    /** CMS journey core types normalized to the five HealthOS behaviours. */
    healthBehaviours: z.array(healthBehaviourSchema).optional(),
    addedAt: z.string(),
    updatedAt: z.string(),
    /** True when the member has a content_completions row for this item. */
    completed: z.boolean().default(false),
    /** Global rating totals and the current member's vote. */
    rating: LibraryRatingSummary.optional(),
});
export const ListLibraryResponse = z.object({
    items: z.array(LibraryItem),
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    hasMore: z.boolean(),
});
export const ListLibraryQuery = z.object({
    query: z.string().trim().max(200).optional(),
    kind: LibraryKind.optional(),
    behaviours: z
        .string()
        .trim()
        .max(200)
        .optional()
        .transform((value) => value
        ? value
            .split(",")
            .map((item) => healthBehaviourSchema.safeParse(item.trim()).data)
            .filter((item) => Boolean(item))
        : []),
    /**
     * CMS tags, comma separated and matched case-insensitively as an OR. CMS
     * tags carry case duplicates (WONDER and Wonder), so a tag is compared
     * lowercased rather than as typed.
     */
    tags: z
        .string()
        .trim()
        .max(400)
        .optional()
        .transform((value) => value
        ? [
            ...new Set(value
                .split(",")
                .map((tag) => tag.trim().toLowerCase())
                .filter((tag) => tag.length > 0 && tag.length <= 60)),
        ].slice(0, 16)
        : []),
    locale: z.string().trim().min(2).max(20).optional(),
    page: z.coerce.number().int().positive().max(10_000).default(1),
    limit: z.coerce.number().int().min(1).max(48).default(24),
});
export const RateLibraryContentParams = z.object({
    contentId: z.string().min(1).max(200),
});
export const RateLibraryContentRequest = z.object({
    vote: ContentRatingVote.nullable(),
});
export const RateLibraryContentResponse = z.object({
    rating: LibraryRatingSummary,
});
/**
 * Heartbeat picks for Collection, scored from the member's goal, focus areas,
 * and current context. Composed on demand and then cached per member in
 * `library_recommendation_shelves`, so a repeat read is a single row lookup;
 * `rating` is layered on at response time and is never part of the cached row.
 */
export const LibraryRecommendation = z.object({
    contentId: z.string().min(1),
    kind: LibraryKind,
    title: z.string(),
    summary: z.string().nullable(),
    imageUrl: z.string().nullable(),
    reason: z.string(),
    metadata: LibraryMetadata,
    dietaryLabels: z.array(z.string()).default([]),
    healthBehaviours: z.array(healthBehaviourSchema).optional(),
    rating: LibraryRatingSummary.optional(),
});
export const ListLibraryRecommendationsResponse = z.object({
    items: z.array(LibraryRecommendation),
    why: z.string().nullable(),
});
export const RecordRecommendationExposureRequest = z.object({
    contentIds: z.array(z.string().min(1).max(200)).min(1).max(4),
    interaction: z.enum(["shown", "opened"]),
});
export const RecordRecommendationExposureResponse = z.object({ ok: z.literal(true) });
// Content completions ("Made this"): a user-recorded completion of a
// library item (a Reset, Recipe, Article, Story, or RoleModel). The
// contentId is the stable library content id; kind mirrors the library
// item kinds. photoAssetId, when set, references a community
// asset the user uploaded alongside the completion.
export const ContentCompletion = z.object({
    id: z.string().uuid(),
    contentId: z.string().min(1),
    kind: LibraryKind,
    rating: z.number().int().min(1).max(5).nullable(),
    notes: z.string().max(2000).nullable(),
    photoAssetId: z.string().uuid().nullable(),
    occurredAt: z.string(),
    createdAt: z.string(),
});
export const CreateContentCompletionRequest = z.object({
    kind: LibraryKind,
    rating: z.number().int().min(1).max(5).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    photoAssetId: z.string().uuid().nullable().optional(),
    occurredAt: z.string().datetime().optional(),
});
export const CreateContentCompletionResponse = z.object({
    completion: ContentCompletion,
});
export const ListContentCompletionsResponse = z.object({
    completions: z.array(ContentCompletion),
});
export const ListUserContentCompletionsQuery = z.object({
    kind: LibraryKind.optional(),
});
/**
 * A completion enriched with the content title (joined from the content
 * index at read time; titles are not stored on the completion row). Null
 * when the content is no longer indexed.
 */
export const UserContentCompletion = ContentCompletion.extend({
    title: z.string().nullable(),
});
export const ListUserContentCompletionsResponse = z.object({
    completions: z.array(UserContentCompletion),
});
/**
 * A Thrive CMS content collection: an editor's ordered list, such as
 * "Breathing Techniques" or "Bedtime Stories". `itemCount` counts what the
 * member can actually open, after access and publish filtering.
 */
export const LibraryCollectionSummary = z.object({
    id: z.string().min(1),
    title: z.string(),
    subtitle: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    itemCount: z.number().int().nonnegative(),
});
export const GetLibraryCollectionParams = z.object({
    collectionId: z.string().trim().min(1).max(200),
});
export const GetLibraryCollectionQuery = z.object({
    page: z.coerce.number().int().positive().max(10_000).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(100),
});
/** The collection's items in editorial order, each a regular library item. */
export const GetLibraryCollectionResponse = z.object({
    collection: LibraryCollectionSummary,
    items: z.array(LibraryItem),
    page: z.number().int().positive(),
    limit: z.number().int().positive(),
    /** More entitled items follow this page; `collection.itemCount` is the total. */
    hasMore: z.boolean(),
});
/** A CMS content hub (Sleep, Stress, Movement, ...) and its collections, in order. */
export const LibraryHub = z.object({
    id: z.string().min(1),
    title: z.string(),
    subtitle: z.string().nullable(),
    iconUrl: z.string().nullable(),
    collections: z.array(LibraryCollectionSummary),
});
export const ListLibraryHubsResponse = z.object({ hubs: z.array(LibraryHub) });
//# sourceMappingURL=library.js.map